import React, { useCallback, useEffect, useMemo, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { CheckCircle, XCircle, Clock, AlertTriangle } from "lucide-react";
import { useAuth } from "@/lib/auth";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

const STATUS_TABS = [
  { key: "verification_pending", label: "To review",       icon: Clock,        color: "bg-amber-100 text-amber-800" },
  { key: "verified",             label: "Verified",        icon: CheckCircle,  color: "bg-emerald-100 text-emerald-800" },
  { key: "additional_proof_required", label: "Proof pending", icon: AlertTriangle, color: "bg-orange-100 text-orange-800" },
  { key: "amount_mismatch",      label: "Mismatch",        icon: AlertTriangle, color: "bg-orange-100 text-orange-800" },
  { key: "partially_paid",       label: "Partial",         icon: AlertTriangle, color: "bg-orange-100 text-orange-800" },
  { key: "on_hold",              label: "On hold",         icon: Clock,        color: "bg-slate-100 text-slate-800" },
  { key: "rejected",             label: "Rejected",        icon: XCircle,      color: "bg-red-100 text-red-800" },
  { key: "refunded",             label: "Refunded",        icon: XCircle,      color: "bg-slate-100 text-slate-800" },
  { key: "cancelled",            label: "Cancelled",       icon: XCircle,      color: "bg-slate-100 text-slate-800" },
];

export default function AdminPartnerPayments() {
  const { user } = useAuth();
  const [tab, setTab] = useState("verification_pending");
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/partner-payments", { params: { status: tab, q } });
      setRows(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load payments");
    } finally { setLoading(false); }
  }, [tab, q]);
  useEffect(() => { load(); }, [load]);

  return (
    <AppLayout>
      <div className="p-6" data-testid="admin-payments-page">
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <div className="miro-overline">Approval Centre</div>
            <h1 className="font-display font-black text-3xl tracking-tight text-[var(--miro-navy)]">
              Partner payment verification
            </h1>
          </div>
          <Input placeholder="Search reference / UTR"
                 value={q} onChange={(e) => setQ(e.target.value)}
                 data-testid="payments-search"
                 className="max-w-xs h-10"/>
        </div>

        <div className="flex gap-2 overflow-x-auto mb-4">
          {STATUS_TABS.map((t) => (
            <button key={t.key}
                    data-testid={`payments-tab-${t.key}`}
                    onClick={() => setTab(t.key)}
                    className={`text-xs px-3 py-1.5 rounded-full font-semibold whitespace-nowrap flex items-center gap-1.5
                      ${tab === t.key ? "bg-[var(--miro-navy)] text-white" : `${t.color} hover:opacity-80`}`}>
              <t.icon size={12}/> {t.label}
            </button>
          ))}
        </div>

        <div className="bg-white border border-[var(--miro-border)] rounded-xl overflow-hidden">
          {loading ? (
            <div className="p-6 text-sm text-[var(--miro-text-muted)]">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-center text-sm text-[var(--miro-text-muted)]">No payments in this bucket.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[var(--miro-bg)] text-[11px] uppercase tracking-widest text-[var(--miro-text-muted)]">
                <tr>
                  <th className="text-left p-3">Reference</th>
                  <th className="text-left p-3">UTR</th>
                  <th className="text-right p-3">Expected</th>
                  <th className="text-right p-3">Submitted</th>
                  <th className="text-left p-3">Method</th>
                  <th className="text-left p-3">Date</th>
                  <th className="text-right p-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-[var(--miro-border)] hover:bg-[var(--miro-bg)]"
                      data-testid={`payment-row-${r.payment_reference}`}>
                    <td className="p-3 font-mono text-xs">{r.payment_reference}</td>
                    <td className="p-3 font-mono text-xs">{r.utr || "—"}</td>
                    <td className="p-3 text-right">{inr(r.amount_expected)}</td>
                    <td className="p-3 text-right">{inr(r.amount_submitted)}</td>
                    <td className="p-3 uppercase text-[11px]">{r.payment_method || "—"}</td>
                    <td className="p-3 text-[11px]">{(r.payment_date || "").slice(0, 10)}</td>
                    <td className="p-3 text-right">
                      <VerifyDialog row={r} onDone={load} isSuper={user?.role === "super_admin" || user?.role === "founder"}/>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AppLayout>
  );
}

function VerifyDialog({ row, onDone, isSuper }) {
  const [open, setOpen] = useState(false);
  const [action, setAction] = useState("verify");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const needsNotes = useMemo(() => ["reject","hold","amount_mismatch","partially_paid","refund","cancel"].includes(action), [action]);

  const submit = async () => {
    if (needsNotes && !notes.trim()) { toast.error("Reviewer notes are required for this action"); return; }
    setSaving(true);
    try {
      await api.post(`/admin/partner-payments/${row.id}/verify`,
        { action, reviewer_notes: notes });
      toast.success(`Payment marked as ${action}`);
      setOpen(false); setNotes(""); setAction("verify");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verify failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`payment-review-${row.payment_reference}`}
                size="sm" variant="outline" className="h-8">Review</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Review payment {row.payment_reference}</DialogTitle></DialogHeader>
        <div className="text-sm text-[var(--miro-text-secondary)] grid grid-cols-2 gap-2">
          <div>Expected</div><div className="text-right font-semibold">{inr(row.amount_expected)}</div>
          <div>Submitted</div><div className="text-right font-semibold">{inr(row.amount_submitted)}</div>
          <div>UTR</div><div className="text-right font-mono text-xs">{row.utr || "—"}</div>
          <div>Method</div><div className="text-right uppercase">{row.payment_method || "—"}</div>
          <div>Sender</div><div className="text-right">{row.sender_account_holder || "—"}</div>
          <div>Payment date</div><div className="text-right">{(row.payment_date || "").slice(0,10)}</div>
        </div>
        {row.applicant_notes && (
          <div className="mt-3 rounded-lg border border-[var(--miro-border)] bg-[var(--miro-bg)] p-3 text-xs">
            <div className="miro-overline">Applicant note</div>
            <div className="mt-1 text-[var(--miro-text-primary)]">{row.applicant_notes}</div>
          </div>
        )}

        <div className="mt-4">
          <Label>Action</Label>
          <Select value={action} onValueChange={setAction}>
            <SelectTrigger data-testid="verify-action-select" className="mt-1 h-10 bg-white"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="verify">Verify payment</SelectItem>
              <SelectItem value="additional_proof">Request additional proof</SelectItem>
              <SelectItem value="amount_mismatch">Amount mismatch</SelectItem>
              <SelectItem value="partially_paid">Partially paid</SelectItem>
              <SelectItem value="hold">Put on hold</SelectItem>
              <SelectItem value="reject">Reject</SelectItem>
              {isSuper && <SelectItem value="refund">Record refund</SelectItem>}
              {isSuper && <SelectItem value="cancel">Cancel</SelectItem>}
            </SelectContent>
          </Select>
        </div>
        <div className="mt-3">
          <Label>Reviewer notes {needsNotes && <span className="text-red-500">*</span>}</Label>
          <Textarea data-testid="verify-notes" rows={3} value={notes}
                    onChange={(e) => setNotes(e.target.value)} className="mt-1"
                    placeholder="Explain your decision (visible only to MIRO team)"/>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button data-testid="verify-submit-btn" onClick={submit} disabled={saving}
                  style={{ background: "var(--miro-navy)" }}>
            {saving ? "Applying…" : "Apply action"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
